import type { AstGroup, AstPredicate, Logic } from '../tools/render/types'

import { buildBuckets, deriveCategories } from './ioc.category-kit'
import type { IocItem } from './ioc.types'

export interface IocAstBuildOptions {
  topLevelLogic?: Logic // default 'or'
  /** When false, skip datasource/dataDisplay directive injection. Default: true. */
  injectDirectives?: boolean
}

/**
 * Build an AST as:
 *   ROOT: OR
 *     - AND [ <pred (field=F1, op=O1, value=single | [list])> ]
 *     - AND [ <pred (field=F2, op=O2, value=single | [list])> ]
 *     - ...
 *
 * Behavior:
 * - Values are deduped by exact string, preserving first-seen order.
 * - If a bucket has exactly 1 unique value → emit a single (string) value.
 * - If a bucket has 2+ values → emit an array of strings.
 * - No normalization of values is performed.
 * - datasource.category and dataDisplay directives are injected by default (injectDirectives: true).
 */
export function buildIocAst(
  iocs: IocItem[],
  opts?: IocAstBuildOptions,
): AstGroup {
  const topLogic: Logic = opts?.topLevelLogic ?? 'or'
  const inject = opts?.injectDirectives !== false // default true

  //    Map IOCs → predicates and bucket by (field, op), merging values
  const buckets = buildBuckets(iocs)

  //    Emit ONE subgroup per bucket, with value collapsed to single or list
  //    Sort buckets for stable, deterministic output (by field, then op)
  const children: AstGroup[] = [...buckets.values()]
    .sort((a, b) => a.field.localeCompare(b.field) || a.op.localeCompare(b.op))
    .map(({ field, op, values, valueType }) => {
      const pred: AstPredicate = {
        kind: 'pred',
        field,
        op,
        value: values.length === 1 ? values[0] : values,
        valueType,
      }
      return {
        kind: 'group' as const,
        logic: 'and' as const,
        children: [pred],
      }
    })

  const root: AstGroup = { kind: 'group', logic: topLogic, children }

  //    Inject datasource + dataDisplay directives derived from the field set
  if (inject) {
    const fields = [...buckets.values()].map((b) => b.field)
    const cats = deriveCategories(fields)
    if (cats.length) {
      root.directives = {
        datasource: { category: cats },
        // when we have multiple categories mapped from a report, use the projection view to avoid missing field values in the group by portion of the aggregation -- bkd 03292026
        dataDisplay: { activeView: 'detail', displayMode: 'project' },
      }
    }
  }

  return root
}
